import React, { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { EmptyState, FormDialog, LoadingState } from '../ui';
import {
  decideVendorRebateRequest,
  listVendorRebateRequests,
  VENDOR_REBATE_REQUESTS_QUERY_KEY,
  type RebateEventRequest,
} from '../../services/rebateRequestsApi';
import { rebateRequestChip } from './rebateRequestShared';

const NS = 'vendor.rebateRequests';

/**
 * Priya's side of event rebate requests: the requests stores sent to this
 * brand and the decision on each. Approving makes the rebate live for that
 * store at once; there is no Motzu review step.
 */
export const VendorRebateRequestsTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<{ request: RebateEventRequest; action: 'approve' | 'deny' } | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: requests, isLoading, isError } = useQuery<RebateEventRequest[]>({
    queryKey: VENDOR_REBATE_REQUESTS_QUERY_KEY,
    queryFn: listVendorRebateRequests,
  });

  const decide = useMutation({
    mutationFn: () => decideVendorRebateRequest(deciding!.request.id, deciding!.action, note.trim()),
    onSuccess: () => {
      setError(null);
      setDeciding(null);
      setNote('');
      queryClient.invalidateQueries({ queryKey: VENDOR_REBATE_REQUESTS_QUERY_KEY });
    },
    onError: (e: any) => setError(e?.message || t(`${NS}.error`)),
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <Alert severity="error">{t(`${NS}.loadError`)}</Alert>;

  const window = (r: RebateEventRequest) => `${new Date(r.startAt).toLocaleDateString()} – ${new Date(r.endAt).toLocaleDateString()}`;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>{t(`${NS}.intro`)}</Alert>
      {error && !deciding && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!requests || requests.length === 0 ? (
        <EmptyState title={t(`${NS}.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {requests.map((r) => (
              <Box key={r.id} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">{r.eventName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.storeOrganizationName || t(`${NS}.unknownStore`)} · {r.brandName} · {t(`${NS}.kind.${r.kind}`)}
                    </Typography>
                  </Box>
                  {rebateRequestChip(r.status, t, NS)}
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t(`${NS}.terms.${r.termsKind}`)} · {formatCurrency(r.amount)} · {window(r)} ·{' '}
                  {r.productIds ? t(`${NS}.products`, { count: r.productIds.length }) : t(`${NS}.wholeBrand`)}
                </Typography>
                {r.requestNote && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{r.requestNote}</Typography>}
                {r.decisionNote && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t(`${NS}.yourNote`, { note: r.decisionNote })}</Typography>}
                {r.status === 'pending' && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" onClick={() => { setError(null); setDeciding({ request: r, action: 'approve' }); }}>{t(`${NS}.approve`)}</Button>
                    <Button size="small" color="inherit" variant="outlined" onClick={() => { setError(null); setDeciding({ request: r, action: 'deny' }); }}>{t(`${NS}.deny`)}</Button>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <FormDialog
        open={!!deciding}
        title={deciding ? t(`${NS}.${deciding.action}Title`, { event: deciding.request.eventName }) : ''}
        onClose={() => { setDeciding(null); setNote(''); setError(null); }}
        onSubmit={() => decide.mutate()}
        submitLabel={deciding ? t(`${NS}.${deciding.action}`) : ''}
        submitColor={deciding?.action === 'deny' ? 'error' : 'primary'}
        loading={decide.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {deciding ? t(`${NS}.${deciding.action}Help`) : ''}
          </Typography>
          <TextField label={t(`${NS}.note`)} value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </FormDialog>
    </Box>
  );
};
